import {
  EPersonalitySections,
  EPersonalityTypes,
  EVisionSections,
  IMatchingVision,
  IPersonalityReportGraph,
  IPersonalityReportPageStructure,
} from 'affiny-core/modules/personalityReport/types';
import { ITheme } from 'affiny-ui/src/theme';

// COMMON HELPERS

export const getPersonalityColor = (
  personalityType: EPersonalityTypes,
  theme: ITheme
) => {
  switch (personalityType) {
    case EPersonalityTypes.CHIVALROUS:
      return theme.colors.chivalrous;
    case EPersonalityTypes.EGALITARIAN:
      return theme.colors.egalitarian;
    case EPersonalityTypes.NEO_ROMANTIC:
      return theme.colors.neo_romantic;
    case EPersonalityTypes.TRADITIONNALIST:
      return theme.colors.traditionnalist;
    default:
      return theme.colors.primary;
  }
};

export const getGraphValue = (graph: IPersonalityReportGraph) =>
  graph.matrices[0]?.viewer.x || 0;

export const getGraphDefinition = (graph: IPersonalityReportGraph) =>
  graph.emphasis[0] || '';

export const getGraphDimension = (graph: IPersonalityReportGraph) =>
  graph.matrices[0]?.dimension;

export const createTranslationArray = (
  nbItems = 0,
  translationBase: string,
  type: string
) =>
  Array(nbItems)
    .fill(null)
    .map((_: null, index: number) => `${translationBase}.${type}.${index}`);

// VALUE TAB HELPERS

export const getGraphsWithAnalysis = (graphs: IPersonalityReportGraph[]) =>
  graphs.filter(graph => graph.analysis.length && graph.emphasis.length);

export const getGraphMotivations = (graph: IPersonalityReportGraph) =>
  graph.emphasis
    .slice(1, graph.emphasis.length)
    .map((motivation: string) => motivation.replace('- ', ''));

// VISION TAB HELPERS

export const VISION_INTRO_GRAPH_ID = EVisionSections.LOVE_PERCEPTION;

export const getGraphMatchingVisions = (
  graph: IPersonalityReportGraph
): IMatchingVision[] => {
  // for the first graph, there are 2 emphasis before the matching elements
  const startIndex = graph.id === VISION_INTRO_GRAPH_ID ? 2 : 1;

  return graph.emphasis
    .slice(startIndex, graph.emphasis.length)
    .map((motivation: string) => {
      const cleanedString = motivation.replace('- ', '');
      const splittedString = cleanedString.split(':');

      return {
        title: splittedString[0] ? splittedString[0].trim() : '',
        description: splittedString[1] ? splittedString[1].trim() : '',
      };
    });
};

export const VISION_PAGE_STRUCTURES: Record<
  EVisionSections,
  IPersonalityReportPageStructure
> = {
  [EVisionSections.LOVE_PERCEPTION]: {
    nbIntro: 3,
    nbGraphItems: 4,
  },
  [EVisionSections.FIDELITY]: {
    nbIntro: 1,
    nbGraphItems: 4,
  },
  [EVisionSections.ROUTINE]: {
    nbIntro: 0,
    nbGraphItems: 4,
  },
  [EVisionSections.SEXUALITY]: {
    nbIntro: 0,
    nbGraphItems: 4,
  },
  [EVisionSections.FAMILY]: {
    nbIntro: 1,
  },
  [EVisionSections.EDUCATION]: {
    nbIntro: 1,
    nbGraphItems: 4,
  },
  [EVisionSections.RELIGION]: {
    nbIntro: 2,
  },
};

// PERSONALITY TAB HELPERS

export const PERSONALITY_PAGE_STRUCTURES: Record<
  EPersonalitySections,
  IPersonalityReportPageStructure
> = {
  [EPersonalitySections.LOVE_NEEDS_INVEST]: {
    nbIntro: 2,
  },
  [EPersonalitySections.PERSONALITY_LONELINESS_VS_CONTACT]: {
    nbIntro: 7,
    nbOutro: 2,
  },
  [EPersonalitySections.RELATION_WITH_OTHERS]: {
    nbIntro: 4,
  },
  [EPersonalitySections.SOULMATES]: {
    nbIntro: 4,
  },
  [EPersonalitySections.COMMUNICATION]: {
    nbIntro: 4,
  },
  [EPersonalitySections.THINK]: {
    nbIntro: 8,
  },
  [EPersonalitySections.EMOTIONAL]: {
    nbIntro: 3,
  },
};

export const NB_BEHAVIOUR_INTRO_PARAGRAPHS = 2;
export const NB_BEHAVIOUR_TYPES = 4;

const LOVE_NEEDS_ID_PREFIX = 'LOVE_NEEDS';
const PERSONALITY_ID_PREFIX = 'PERSONALITY';

const isLoveNeedsGraph = (graph: IPersonalityReportGraph) =>
  graph.id.startsWith(LOVE_NEEDS_ID_PREFIX);
const isPersonalityGraph = (graph: IPersonalityReportGraph) =>
  graph.id.startsWith(PERSONALITY_ID_PREFIX);

export const getLoveNeedsGraphs = (graphs: IPersonalityReportGraph[]) =>
  graphs.filter(isLoveNeedsGraph);
export const getPersonalityGraphs = (graphs: IPersonalityReportGraph[]) =>
  graphs.filter(isPersonalityGraph);
export const getOtherPersonalityGraphs = (graphs: IPersonalityReportGraph[]) =>
  graphs.filter(
    graph => !isLoveNeedsGraph(graph) && !isPersonalityGraph(graph)
  );
